// Cosmic Sage — offline cache for deterministic astrology data.
// Uses localStorage. Birth charts (same DOB/time/place) and a given date's
// panchang are deterministic, so we cache them indefinitely and serve them
// instantly (also when the user is offline).

const PREFIX = "cs:cache:v1:";
const INDEX_KEY = `${PREFIX}__index`;
const MAX_ENTRIES = 60; // keep storage bounded

function stableStringify(obj) {
  if (obj === null || typeof obj !== "object") return JSON.stringify(obj);
  if (Array.isArray(obj)) return "[" + obj.map(stableStringify).join(",") + "]";
  const keys = Object.keys(obj).sort();
  return "{" + keys.map((k) => JSON.stringify(k) + ":" + stableStringify(obj[k])).join(",") + "}";
}

function hash(str) {
  // Lightweight DJB2-like hash → hex
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = (h * 33) ^ str.charCodeAt(i);
  return (h >>> 0).toString(16);
}

export function makeKey(scope, payload) {
  return `${PREFIX}${scope}:${hash(stableStringify(payload))}`;
}

function loadIndex() {
  try {
    return JSON.parse(localStorage.getItem(INDEX_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveIndex(idx) {
  try { localStorage.setItem(INDEX_KEY, JSON.stringify(idx)); } catch { /* quota */ }
}

function touchIndex(key) {
  const idx = loadIndex().filter((k) => k !== key);
  idx.unshift(key);
  while (idx.length > MAX_ENTRIES) {
    const evict = idx.pop();
    try { localStorage.removeItem(evict); } catch { /* ignore */ }
  }
  saveIndex(idx);
}

export function setCache(key, data) {
  try {
    const record = { cachedAt: new Date().toISOString(), data };
    localStorage.setItem(key, JSON.stringify(record));
    touchIndex(key);
  } catch {
    // Quota exceeded — drop oldest and retry once
    const idx = loadIndex();
    while (idx.length) {
      const evict = idx.pop();
      try { localStorage.removeItem(evict); } catch { /* ignore */ }
      try {
        const record = { cachedAt: new Date().toISOString(), data };
        localStorage.setItem(key, JSON.stringify(record));
        idx.unshift(key);
        saveIndex(idx);
        return;
      } catch { /* keep evicting */ }
    }
  }
}

export function getCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const record = JSON.parse(raw);
    return record; // { cachedAt, data }
  } catch {
    return null;
  }
}

/**
 * Run `fetcher` and cache its result under `key`. On network failure, fall
 * back to the cached copy (if any). Returns `{ data, fromCache, cachedAt }`.
 */
export async function withCache(key, fetcher, { staleWhileRevalidate = false } = {}) {
  const cached = getCache(key);
  if (staleWhileRevalidate && cached) {
    // Return cached immediately, refresh in background
    fetcher()
      .then((fresh) => setCache(key, fresh))
      .catch(() => {});
    return { data: cached.data, fromCache: true, cachedAt: cached.cachedAt };
  }
  try {
    const fresh = await fetcher();
    setCache(key, fresh);
    return { data: fresh, fromCache: false, cachedAt: null };
  } catch (err) {
    if (cached) return { data: cached.data, fromCache: true, cachedAt: cached.cachedAt, error: err };
    throw err;
  }
}

export function clearCache() {
  const idx = loadIndex();
  idx.forEach((k) => { try { localStorage.removeItem(k); } catch { /* ignore */ } });
  saveIndex([]);
}

export function cacheSize() {
  return loadIndex().length;
}
